// Pure functions that build chat message objects for the Program Matrix AI Coach wizard.
// Every message always carries a plain-English `content` string fallback so
// ChatPDFDocument.jsx / ChatBotLauncher.jsx's share-to-clipboard (which only read
// message.content/fullContent) keep working unmodified for wizard turns.

import { schoolGradeKeys, normalizeWords } from "./constants"

const botMessage = (type, content, wizardMeta = {}) => ({
  role: "bot",
  type,
  content,
  wizardMeta,
  answered: false,
})

export const buildTextMessage = (content) => ({ role: "bot", content })

export const buildUserReplyMessage = (content) => ({ role: "user", content })

export const buildFoundActivitiesMessage = (activities) => {
  // This overview list mirrors the document's own naming (not Rei's cleaned-up suggestion) so it
  // reads as a table of contents you can cross-reference against what you uploaded — the AI's
  // suggested name only comes up later, per-activity, where both are shown side by side. Falling
  // back to suggested_name here would silently show Rei's invented name as if it were the
  // document's own — say plainly that the document itself has no name for this activity instead.
  // Reassure the user inline that "no name in document" isn't a dead end — the name step for
  // that activity (buildNameDescriptionMessage) still walks them through picking one, with Rei's
  // own suggestion where it can produce one.
  const list = activities
    .map((a, i) => (a.document_activity_name
      ? `${i + 1}. ${a.document_activity_name}`
      : `${
        i + 1
      }. No name in document (If you start with this activity, I can suggest one once we get there.)`))
    .join("\n")
  return buildTextMessage(
    `**I found ${activities.length} activit${
      activities.length === 1 ? "y" : "ies"
    } in this document:**\n${list}`,
  )
}

export const buildStartPromptMessage = () => botMessage(
  "single_choice",
  "I'll walk you through each one, starting with the most complete. Ready?",
  {
    field: "start",
    options: [
      { value: "start", label: "Start with Activity 1 →", style: "primary" },
    ],
  },
)

// Shown when startActivity runs off the end of this batch (activityIndex + 1 is out of bounds)
// but an EARLIER activity is still genuinely unsaved — e.g. the user jumped straight to Activity 2
// of a 2-activity document (see submitStartFreeText) and Activity 1 was never visited at all.
// Without this, that activity would be silently forgotten behind a flat "All activities are done!"
// even though it's sitting right there in the same document just uploaded. `pendingIndex` is
// carried as the button's own value (this prompt isn't tied to any single activityRef, so there's
// no wizardMeta.activityRef to route through) — onWizardRespond reads it directly to resume at
// that exact activity, same as typing its number/name would.
export const buildResumePendingActivityMessage = (pendingIndex, total, name) => botMessage(
  "single_choice",
  `Activity ${pendingIndex + 1} of ${total} ("${name}") from this document is still pending.`,
  {
    field: "resume_activity",
    options: [
      {
        value: String(pendingIndex),
        label: `Start with Activity ${pendingIndex + 1} →`,
        style: "primary",
      },
    ],
  },
)

// incompleteByCategory: { [category]: [{ id, activity, missing_fields }] } — from
// GET program_matrix_items/incomplete_summary. Shown once, after every activity from THIS
// upload is done, to remind the user about OTHER activities already on their matrix that are
// still missing required fields. Per spec, this only informs — no per-field AI suggestions are
// offered for these, unlike the document-extraction flow above.
export const buildIncompleteExistingActivitiesMessage = (
  incompleteByCategory,
) => {
  // One block per category: a bold "Category" header, then each of its activities numbered
  // "1)", "2)", etc. underneath, with that activity's own missing fields comma-separated on the
  // same line.
  //
  // Numbers are embedded as literal bold text ("**1)**"), NOT real "1) " markdown list syntax —
  // ChatBotUI's OWN markdown renderer needs a custom `li` component specifically to manually
  // re-add "N. " text (see markdownComponents.li there) because this site's CSS strips native
  // <ol>/<ul> marker numbers; WizardMessage.jsx has no such override, so a real markdown list
  // here would render with its numbers invisible. Leading "**" also keeps remark from parsing
  // "1)" as a real list marker in the first place (a bare "1) " at the start of a line would be).
  // Activity lines within one category are joined with a hard line break ("  \n", two trailing
  // spaces — standard CommonMark for a break within the SAME paragraph) rather than a blank line
  // between them, so a category's activities stay one tight block instead of stacking a separate
  // paragraph's margin for every single activity.
  const totalCount = Object.values(incompleteByCategory).reduce(
    (sum, items) => sum + items.length,
    0,
  )
  const blocks = Object.entries(incompleteByCategory).map(
    ([category, items]) => {
      const header = `**${category}**`
      const itemLines = items
        .map(
          (item, i) => `**${i + 1})** ${
            item.activity
          } — missing: ${item.missing_fields.join(", ")}`,
        )
        .join("  \n")
      return `${header}\n${itemLines}`
    },
  )

  const content = [
    `You also have ${totalCount} incomplete activit${
      totalCount === 1 ? "y" : "ies"
    } already in your matrix:`,
    blocks.join("\n\n"),
    "Would you like me to help you fill these in now, or would you prefer to do it yourself?",
  ].join("\n\n")

  return botMessage("single_choice", content, {
    field: "existing_incomplete_summary",
    options: [
      { value: "help", label: "Help me fill these in", style: "primary" },
      { value: "myself", label: "I'll do it myself", style: "outline" },
    ],
  })
}

export const buildActivityBannerMessage = (activityIndex, total, name) => botMessage("banner", `Activity ${activityIndex + 1} of ${total} - ${name}`, {
  activityIndex,
  total,
  name,
})

// Shown instead of a flat rejection when a typed answer to an enum-backed question (domain/tier/
// years) is close enough to exactly one valid option to read as a typo (see the fuzzy-match
// fallback in validateFreeTextAnswer's matchAgainstList) — "Yes" applies `suggestion` exactly as
// if it had been typed correctly; "No" re-asks the original question (see resolveTypoConfirmation
// in useProgramMatrixWizard.js) rather than leaving the user stuck. `originalField`/`suggestion`
// are carried in wizardMeta (not just held in a ref) so a chat-drawer close/reopen can still
// resolve this prompt correctly — see the rehydration effect in useProgramMatrixWizard.js.
export const buildTypoConfirmationMessage = (
  activityRef,
  originalField,
  typedText,
  suggestion,
) => botMessage(
  "single_choice",
  `I didn't recognize "${typedText}" — did you mean **${suggestion}**?`,
  {
    activityRef,
    field: "typo_confirm",
    originalField,
    suggestion,
    options: [
      { value: "yes", label: `Yes, use "${suggestion}"`, style: "primary" },
      { value: "no", label: "No, let me choose again", style: "outline" },
    ],
  },
)

// Surfaces an "ambiguous" match (a plausible but not-confident candidate, or more than one
// exact-name hit in the same category) before any field questions start, so the user decides
// whether to update that existing activity or proceed as a new one — ambiguous matches are never
// applied silently.
export const buildConfirmMatchMessage = (activity) => {
  const candidates = (activity.match && activity.match.candidates) || []
  const options = candidates.map((c) => ({
    value: String(c.id),
    label: `Yes, that's my "${c.activity}"`,
    style: "primary",
  }))
  options.push({
    value: "__new__",
    label: "No, this is a new activity",
    style: "outline",
  })

  const candidateNames = candidates.map((c) => `"${c.activity}"`).join(" or ")

  return botMessage(
    "single_choice",
    `This looks like it might already be in your Matrix as ${candidateNames}. Is this the same activity?`,
    { activityRef: activity.client_ref, field: "confirm_match", options },
  )
}

// `category` is a fixed, validated enum on ProgramMatrixItem — every valid category is already a
// button here, but the wizard also accepts a typed answer directly (no dedicated "type it myself"
// button needed; see TYPED_INPUT_FIELDS/currentFieldRef in useProgramMatrixWizard), checked via
// validateFreeTextAnswer against this same list before advancing, instead of accepting a value
// the backend would reject.
export const buildDomainQuestionMessage = (activity) => {
  const categories = (window.gon && window.gon.CATEGORIES) || [
    activity.suggested_category,
  ]
  const documentDomain = activity.document_domain
  const invalidDocumentDomain = activity.invalid_document_domain
  const aiDomain = activity.suggested_category
  const domainsDiffer = !!documentDomain && !!aiDomain && documentDomain !== aiDomain

  // Same set of category buttons either way — when the document's own domain and Rei's judgment
  // disagree, this just labels which button is which source instead of redesigning the widget.
  const options = categories.map((cat) => {
    if (domainsDiffer && cat === documentDomain) {
      return {
        value: cat,
        label: `${cat} (from your document)`,
        style: "outline",
      }
    }
    if (cat === aiDomain) {
      return {
        value: cat,
        label:
          domainsDiffer || invalidDocumentDomain
            ? `${cat} (Rei suggests)`
            : `Yes, ${cat}`,
        style: "primary",
      }
    }
    return { value: cat, label: cat, style: "outline" }
  })

  let content
  if (domainsDiffer) {
    content = `Your document lists this activity's domain as **${documentDomain}**, but based on its overall details, Rei suggests **${aiDomain}** instead. Which should we use?`
  } else if (documentDomain && aiDomain) {
    content = `Your document lists this activity under **${documentDomain}**, and Rei agrees this is the right domain.`
  } else if (invalidDocumentDomain) {
    content = `Your document lists this activity's domain as "${invalidDocumentDomain}", which isn't one of your school's categories. Based on its overall details, Rei suggests **${aiDomain}** instead. Which should we use?`
  } else {
    // The only way to reach this branch is documentDomain AND invalidDocumentDomain both being
    // falsy — per finalize_activity, that means the document's domain column was genuinely blank
    // for this activity (a non-blank-but-invalid value would have set invalidDocumentDomain
    // instead). Say so explicitly rather than silently jumping to Rei's own suggestion, same as
    // buildTierQuestionMessage's equivalent "document doesn't list a tier" case below.
    content = `Your document doesn't specify a domain for this activity. Based on its overall details, Rei suggests **${aiDomain}**. Do you agree?`
  }

  return botMessage("single_choice", content, {
    activityRef: activity.client_ref,
    field: "domain",
    options,
  })
}

// Tier descriptions stay attached to each option (rendered as a second line inside its
// button by SingleChoiceButtons) since "Tertiary"/"Universal"/"Targeted" aren't self-explanatory
// the way domain category names are.
const TIER_DESCRIPTIONS = {
  Tertiary: "High risk students have access to individualized Support.",
  Universal: "The support is designed for all students to have access.",
  Targeted:
    "For students who are selected or otherwise demonstrate a risk factor to gain access to the support.",
}

// Always displayed in this fixed order, regardless of which tier is recommended — the
// recommended one is still highlighted via its label/style below, just not repositioned.
const TIER_ORDER = ["Universal", "Targeted", "Tertiary"]

export const buildTierQuestionMessage = (activity) => {
  // document_tier_of_support is THIS upload's own validated tier column — not
  // activity.match.existing_item.tier_of_support, which is the tier of a DIFFERENT, already-
  // saved activity in this school's matrix that just happened to match by name/category. Those
  // are unrelated data sources; conflating them previously caused "Your document lists..." to
  // show a tier the current document never actually specified, whenever a same-named activity
  // with a tier already existed in this particular school's matrix.
  const documentTier = activity.document_tier_of_support
  const aiTier = activity.suggested_tier_of_support
  const invalidDocumentTier = activity.invalid_document_tier_of_support
  const tiersDiffer = !!documentTier && !!aiTier && documentTier !== aiTier
  // Only a genuine document+Rei match earns "Yes, X" — a disagreement is Rei's call, not a
  // document fact.
  const isGenuineAgreement = !!documentTier && !!aiTier && documentTier === aiTier
  // No "(Rei suggests)" pre-selection anywhere in this field — only a genuine document+Rei
  // agreement gets a highlighted button (as a plain "Yes, X" confirmation of the document's own
  // value). Every other case (disagreement, blank document, invalid document text) shows all
  // three tiers as neutral, unselected options for the user to pick from themselves.
  const recommendedTier = isGenuineAgreement ? documentTier : null

  // An existing matrix item being filled in manually (see activityFromExistingItem in
  // useProgramMatrixWizard.js) has no document behind it, and this step only runs when its own
  // tier is genuinely missing (documentTier/aiTier/invalidDocumentTier are always unset here) —
  // so it's a single, simple, source-agnostic prompt rather than any of the document branches
  // below.
  let content
  if (activity.isExistingItem) {
    content = "This activity doesn't have a tier of support set yet. Which tier of support does it serve?"
  } else if (tiersDiffer) {
    content = `Your document lists this activity's tier of support as **${documentTier}**, but based on the description, Rei suggests **${aiTier}** instead. Which should we use?`
  } else if (documentTier && aiTier) {
    content = `Your document lists this activity's tier of support as **${documentTier}**.`
  } else if (documentTier) {
    content = `Your document lists this activity's tier of support as **${documentTier}**.`
  } else if (invalidDocumentTier) {
    content = `Your document lists this activity's tier of support as "${invalidDocumentTier}", which isn't a valid tier. Which tier of support does this activity serve?`
  } else {
    content = "Your document doesn't list a tier of support for this activity. Which tier of support does this activity serve?"
  }

  // Same set of tier buttons either way — when the document's own tier and Rei's judgment
  // disagree, this just labels which button is which source instead of redesigning the widget.
  const options = TIER_ORDER.map((tier) => {
    if (tiersDiffer && tier === documentTier) {
      return {
        value: tier,
        label: `${tier} (from your document)`,
        description: TIER_DESCRIPTIONS[tier],
        style: "outline",
      }
    }
    if (recommendedTier && tier === recommendedTier) {
      return {
        value: tier,
        label: `Yes, ${tier}`,
        description: TIER_DESCRIPTIONS[tier],
        style: "primary",
      }
    }
    return {
      value: tier,
      label: tier,
      description: TIER_DESCRIPTIONS[tier],
      style: "outline",
    }
  })

  return botMessage("single_choice", content, {
    activityRef: activity.client_ref,
    field: "tier",
    options,
  })
}

export const buildGradesQuestionMessage = (activity, tier) => {
  const documentGrades = activity.suggested_grade_levels || []
  const validGrades = schoolGradeKeys()
  // Deliberately does NOT seed from activity.match.existing_item.internal_selected_grades (a
  // previously-saved record's own counts) — silently blending in old dashboard numbers next to
  // this upload's own data made it impossible to tell which number came from where. The picker
  // shows only what THIS document provides (per-grade if available, else one aggregate total
  // below); a grade/field the document doesn't cover starts blank rather than borrowing a
  // number from elsewhere.
  const documentStudentCounts = Number.isFinite(activity.suggested_students_can_be_supported)
    || Number.isFinite(activity.suggested_students_being_supported)
    ? {
      can_be_supported: activity.suggested_students_can_be_supported,
      being_supported: activity.suggested_students_being_supported,
    }
    : null
  const matchedGrades = documentGrades.filter((grade) => validGrades.includes(grade))
  const unmatchedGrades = documentGrades.filter(
    (grade) => !validGrades.includes(grade),
  )
  const noValidMatch = documentGrades.length > 0 && matchedGrades.length === 0
  const isUniversal = tier === "Universal"

  // An existing matrix item being filled in manually (see activityFromExistingItem in
  // useProgramMatrixWizard.js) has no document behind it — "your document"/"selected below are
  // from your document" framing would be wrong, so this uses source-agnostic wording instead.
  // The grade-matching logic above still applies either way: this activity's OWN already-set
  // grades (if any — the grades step only runs for an existing item missing grades and/or
  // population, so it can already have SOME grades set, just incomplete population) are
  // validated against the school's configured list the same way regardless of where they came
  // from.
  let prefix = ""
  if (activity.isExistingItem) {
    if (noValidMatch) {
      prefix = `This activity lists grade(s) **${documentGrades.join(
        ", ",
      )}**, but none of those match this school's configured grade levels (**${validGrades.join(
        ", ",
      )}**). `
    } else if (unmatchedGrades.length > 0) {
      prefix = `This activity also lists grade(s) **${unmatchedGrades.join(
        ", ",
      )}**, which aren't part of this school's configured grade levels, so they've been left out. `
    }
  } else if (documentGrades.length === 0) {
    prefix = "Your document didn't specify grade levels for this activity. "
  } else if (noValidMatch) {
    prefix = `Your document lists grade(s) **${documentGrades.join(
      ", ",
    )}**, but none of those match this school's configured grade levels (**${validGrades.join(
      ", ",
    )}**). `
  } else if (unmatchedGrades.length > 0) {
    prefix = `Your document also mentioned grade(s) **${unmatchedGrades.join(
      ", ",
    )}**, which aren't part of this school's configured grade levels, so they've been left out. `
  }

  // A Universal activity is open to every student in the grades it covers, so — same as the
  // Add Activity form — there's nothing to ask: student counts come from each grade's own
  // configured population, not manual entry.
  const gradeInstruction = activity.isExistingItem
    ? matchedGrades.length > 0
      ? "The grade levels already selected below can be updated if needed."
      : "Please select the grade levels for this activity below."
    : "The grade levels selected below are from your document — update them if needed."
  const question = isUniversal
    ? `${prefix}${gradeInstruction} Since this is a Universal activity, we'll use each grade's full population automatically.`
    : `${prefix}${gradeInstruction} Since this is a ${tier} activity, also provide student counts:`

  return botMessage("grade_picker", question, {
    activityRef: activity.client_ref,
    field: "grades",
    suggestedGrades: matchedGrades,
    validGrades,
    documentGrades,
    noValidMatch,
    isUniversal,
    documentStudentCounts,
    // Per-grade breakdown from the document itself (e.g. "100,100,99" positionally matched
    // against "1,2,3") — preferred over documentStudentCounts (one aggregate total for the
    // whole activity) for any grade it actually covers.
    documentGradePopulations: activity.suggested_grade_populations || {},
  })
}

// Shown after a grade's population is fully captured via the chat-driven "how many can/being
// supported" exchange (see the "grade_population" sub-flow in useProgramMatrixWizard.js), and
// again whenever a typed grade or population answer is rejected — a recap of every grade
// collected SO FAR for this activity, each pre-filled with whatever counts it already has (blank
// for one still in progress), so the user can see the full picture instead of having to remember
// it or reconstruct it from scratch. Its own Submit still works normally as an alternative way to
// finish this step alongside the chat-driven "add another grade?" flow — advanceField's "grades"
// case accumulates onto this activity's already-collected grades rather than replacing them
// outright once any exist (see the note there), so clicking an extra grade button here and
// submitting adds to this recap rather than wiping it.
export const buildGradesProgressCardMessage = (activity, collected) => {
  const validGrades = schoolGradeKeys()
  const isUniversal = collected.tier === "Universal"
  const grades = collected.grades || []
  const documentGradePopulations = (collected.internalSelectedGrades || []).reduce(
    (map, g) => ({
      ...map,
      [g.grade]: {
        can_be_supported: g.students_can_be_supported,
        being_supported: g.students_being_supported,
      },
    }),
    {},
  )
  const content = grades.length > 0
    ? "Here's what you've added so far:"
    : "No grades added yet."

  return botMessage("grade_picker", content, {
    activityRef: activity.client_ref,
    field: "grades",
    suggestedGrades: grades,
    validGrades,
    documentGrades: grades,
    noValidMatch: false,
    isUniversal,
    documentStudentCounts: null,
    documentGradePopulations,
  })
}

// Shown once a grade's population is fully captured, asking whether to walk through another
// grade (repeating the same grade -> can-be-supported -> being-supported exchange) or move on to
// the next field — see resolveAddAnotherGrade in useProgramMatrixWizard.js.
export const buildAddAnotherGradeMessage = (activityRef) => botMessage(
  "single_choice",
  "Would you like to add another grade?",
  {
    activityRef,
    field: "grades_add_another",
    options: [
      { value: "yes", label: "Yes, add another grade", style: "primary" },
      { value: "no", label: "No, that's all", style: "outline" },
    ],
  },
)

// Shown when the document mentions grade(s) this school hasn't configured — informational only,
// with no Add/Skip choice: a grade with no real configured population can't be validly tracked
// for this activity at all, so it's automatically left out (see continueGradesFlow in
// useProgramMatrixWizard.js) rather than offered as something the user could force-add anyway.
export const buildUnmatchedGradesInfoMessage = (unmatchedGrades) => {
  const plural = unmatchedGrades.length > 1
  const gradeList = unmatchedGrades.join(", ")
  return buildTextMessage(
    `Your document also lists grade${plural ? "s" : ""} **${gradeList}**, which ${
      plural ? "aren't" : "isn't"
    } part of your school's configured grade levels, so ${
      plural ? "they've" : "it's"
    } been left out.`,
  )
}

export const buildYearsQuestionMessage = (activity) => {
  const suggested = activity.suggested_length_of_time
  const invalidDocumentLength = activity.invalid_document_length_of_time
  const lengthsOfTime = (window.gon && window.gon.LENGTHS_OF_TIME) || [
    "0-1 year",
    "2-4 years",
    "5+ years",
  ]

  // An existing matrix item being filled in manually has no document behind it, and this step
  // only runs when its own length of time is genuinely missing (suggested/invalidDocumentLength
  // are always unset here) — see buildTierQuestionMessage's identical note above.
  let content
  if (activity.isExistingItem) {
    content = "This activity doesn't have a length of time set yet. How long has your school been running this?"
  } else if (suggested && invalidDocumentLength) {
    content = `Your document lists this activity's length of time as "${invalidDocumentLength}", which isn't supported. Based on the description, Rei suggests **${suggested}** instead. Does that sound right?`
  } else if (suggested) {
    content = `Your document says this has been running for **${suggested}**. Does that sound right?`
  } else if (invalidDocumentLength) {
    content = `Your document lists this activity's length of time as "${invalidDocumentLength}", which isn't supported. How long has your school been running this?`
  } else {
    content = "Your document didn't specify how long this has been running. How long has your school been running this?"
  }

  // Same pattern as the tier buttons: recommendation promoted to the top and highlighted,
  // rest of the options listed underneath as plain alternatives.
  const orderedLengths = suggested
    ? [suggested, ...lengthsOfTime.filter((length) => length !== suggested)]
    : lengthsOfTime

  const options = orderedLengths.map((length) => (length === suggested
    ? {
      value: length,
      label: invalidDocumentLength
        ? `${length} (Rei suggests)`
        : `Yes, ${length}`,
      style: "primary",
    }
    : { value: length, label: length, style: "outline" }))

  return botMessage("single_choice", content, {
    activityRef: activity.client_ref,
    field: "years",
    options,
  })
}

// No fallback offered when the AI didn't produce a name — accepting a blank/"null" name would
// silently save an unidentifiable activity, since ProgramMatrixItem has no presence validation
// on `activity` (blank names are a deliberate, supported draft state elsewhere in the app).
export const buildNameDescriptionMessage = (activity) => {
  const documentName = activity.document_activity_name
  const aiName = activity.suggested_name
  // A null/blank aiName does NOT reliably mean suggested_description is ALSO unusable — per the
  // extraction prompt, Rei is supposed to fall back to deriving a name from a usable description
  // when the document's own name isn't one, but doesn't always honor that fallback (it can just
  // leave suggested_name blank instead). sanitized_name_and_description in the extraction service
  // nulls suggested_name/suggested_description independently based on each one's OWN content, so
  // checking aiDescription directly below is the only way to know whether the description is
  // actually usable, rather than assuming it never is whenever aiName comes back empty.
  const aiDescription = activity.suggested_description

  if (!documentName && !aiName) {
    // An existing matrix item being filled in manually has no document behind it at all — this
    // step only asks about the name; description is its own separate step regardless of whether
    // it's also missing, so it's never mentioned here.
    let content
    if (activity.isExistingItem) {
      content = "This activity doesn't have an activity name yet. What should I call it?"
    } else {
      const descriptionState = aiDescription
        ? "Rei will use its description to help in the next step"
        : activity.document_description
          ? "its description isn't usable either"
          : "its description is empty too"
      content = `Your document's Activity Name column is empty for this activity, and Rei couldn't suggest one — ${descriptionState}. What should I call it?`
    }
    return botMessage("single_choice", content, {
      activityRef: activity.client_ref,
      field: "name_description",
      options: [
        {
          value: "__other__",
          label: "Type an activity name",
          style: "primary",
          freeText: true,
        },
      ],
    })
  }

  // documentName present but aiName null means the document's own name (e.g. "n/a") isn't usable
  // as written. Never fall through to the "agrees it's already clear" message below for this
  // case, since that would misrepresent an unusable placeholder as a name Rei actually endorsed.
  if (documentName && !aiName) {
    const reason = aiDescription
      ? "Rei wasn't able to turn its description into a name, but you'll get a chance to review that description next"
      : "there wasn't enough in this activity's description or its \"Describe how this activity promotes/reinforces this\" entry either for Rei to suggest a better one"
    return botMessage(
      "single_choice",
      `Your document names this activity **${documentName}**, which isn't usable as a name — ${reason}. What should I call it?`,
      {
        activityRef: activity.client_ref,
        field: "name_description",
        options: [
          {
            value: documentName,
            label: `Keep "${documentName}"`,
            style: "outline",
          },
          {
            value: "__other__",
            label: "Type an activity name",
            style: "primary",
            freeText: true,
          },
        ],
      },
    )
  }

  // The "Edit" option below only lets you retype the name — description gets its own dedicated
  // step right after this one (see buildDescriptionQuestionMessage), which already shows and
  // lets you edit it there, so this message stays scoped to the name only.
  // Compared via normalizeWords (not a byte-for-byte !==), same as the description-reuse check
  // below — Rei reusing the document's own name with only trivial polish (e.g. "Meetings with
  // staff" -> "Meetings with Staff") is Rei agreeing it's already clear, not a real alternative
  // worth presenting as a separate button next to the original.
  const namesDiffer = !!documentName
    && !!aiName
    && normalizeWords(documentName) !== normalizeWords(aiName)
  // A name over 3 words reads like a sentence/description rather than a title — Rei may still
  // have genuinely endorsed it (not overridden it for being unusable), just also offering a
  // tighter alternative. That's a different situation from namesDiffer's usual "document's name
  // vs. a wholesale replacement" framing, so it gets its own wording below rather than being
  // presented as a straight either/or between two unrelated names.
  const documentNameIsLong = !!documentName && documentName.trim().split(/\s+/).length > 3

  const options = namesDiffer
    ? documentNameIsLong
      ? [
        {
          value: documentName,
          label: `Keep my name (${documentName})`,
          style: "primary",
        },
        {
          value: aiName,
          label: `Use Rei's shorter version (${aiName})`,
          style: "outline",
        },
      ]
      : [
        {
          value: documentName,
          label: `Use document name (${documentName})`,
          style: "outline",
        },
        {
          value: aiName,
          label: `Use Rei's suggestion (${aiName})`,
          style: "primary",
        },
      ]
    : [
      {
        value: aiName || documentName,
        label: "Yes, use this",
        style: "primary",
      },
    ]
  options.push({
    value: "__other__",
    label: "Edit - Please type a response.",
    style: "outline",
    freeText: true,
  })

  // When both exist but agree, repeating the identical value under two labels says nothing new —
  // a plain "Rei agrees" line is more useful than a redundant compare view for that case.
  // Rei's name judgment is about document_activity_name's own clarity/wording — it has nothing
  // to do with suggested_description, so these must never attribute it to "the description"
  // (misleading on its own). Description handling — including any heads-up about it — belongs
  // entirely to the dedicated step right after this one (see buildDescriptionQuestionMessage);
  // this step used to also flag "your document's description wasn't usable as written" whenever
  // suggested_description simply differed from document_description, but that difference isn't a
  // reliable usability signal — GPT is instructed to reuse a genuinely clear document_description
  // verbatim, but doesn't always honor that, so a mismatch here just as often means GPT paraphrased
  // a perfectly fine description as it does the description actually being unusable.
  const documentDescription = activity.document_description

  // When Rei's name diverges from the document's own Activity Name value, ground the suggestion
  // in the actual excerpt Rei read it from — otherwise the suggestion looks invented from nothing,
  // especially when the document's own name field is a placeholder like "n/a". source_excerpt is
  // just "one short sentence quoted from the document" per the extraction prompt — it isn't tied
  // to any particular column (it's often actually the Description column, not the "Describe how
  // this activity promotes/reinforces this" one), so the wording here must stay column-agnostic
  // instead of naming a specific field it may not have come from.
  const sourceExcerpt = activity.source_excerpt
  // source_excerpt itself carries no metadata on which column it came from, but we can usually
  // tell by checking whether it's actually a substring of a field we DO have — description is
  // checked first since that's what source_excerpt is most often quoting in practice.
  // normalizeWords (not just whitespace collapsing) is needed here because GPT's phrasing of
  // source_excerpt varies run to run beyond whitespace — e.g. quoting the same sentence with
  // different capitalization, "relationship" -> "relationships", or an added trailing period.
  // Only falls back to the generic "your document" phrasing when neither field contains it.
  const documentConnectionExplanationForExcerpt = activity.document_connection_explanation
  const excerptSource = (() => {
    if (!sourceExcerpt) return null
    const normalizedExcerpt = normalizeWords(sourceExcerpt)
    if (
      documentDescription
      && normalizeWords(documentDescription).includes(normalizedExcerpt)
    ) {
      return "your document's description"
    }
    if (
      documentConnectionExplanationForExcerpt
      && normalizeWords(documentConnectionExplanationForExcerpt).includes(
        normalizedExcerpt,
      )
    ) {
      return "your document's \"Describe how this activity promotes/reinforces this\" entry"
    }
    return "your document"
  })()
  // Belt-and-suspenders check: source_excerpt and suggested_name are independently generated by
  // GPT, so nothing guarantees they're actually related — e.g. a gibberish document_description
  // ("abcSK") can end up copied straight into suggested_name while source_excerpt quotes a
  // completely different, unrelated field. Only attribute the name to the excerpt when they
  // actually share real content; otherwise the "based on X" framing is a false citation.
  const excerptGroundsName = !!sourceExcerpt
    && !!aiName
    && (() => {
      const nameWords = new Set(
        normalizeWords(aiName)
          .split(" ")
          .filter((w) => w.length > 2),
      )
      const excerptWords = new Set(
        normalizeWords(sourceExcerpt)
          .split(" ")
          .filter((w) => w.length > 2),
      )
      return [...nameWords].some((word) => excerptWords.has(word))
    })()
  const aiNameLine = excerptGroundsName
    ? `Rei suggests **${aiName}**, based on ${excerptSource}.`
    : `**Rei suggests:** ${aiName}`

  let content
  if (namesDiffer && documentNameIsLong) {
    content = `Your document names this activity **${documentName}**, and Rei agrees this works. If you'd like something more concise, Rei suggests **${aiName}** instead.`
  } else if (namesDiffer) {
    content = `**Document's Activity Name:** ${documentName}\n\n${aiNameLine}\nWhich name should we use?`
  } else if (documentName && aiName) {
    content = `Your document names this activity **${documentName}**, and Rei agrees this name is already clear.`
  } else {
    // Only reachable when documentName is falsy and aiName is truthy — state that plainly and
    // explain what Rei's suggestion is actually grounded in (aiNameLine), same as namesDiffer
    // above, instead of bundling in a description preview that duplicates the dedicated step
    // right after this one.
    content = `Your document doesn't have a name for this activity.\n\n${aiNameLine}`
  }

  return botMessage("single_choice", content, {
    activityRef: activity.client_ref,
    field: "name_description",
    options,
  })
}

// Its own step (rather than bundled into the name question) specifically so there's a real
// free-text control for editing it — the name step above only has one for the name itself.
export const buildDescriptionQuestionMessage = (activity) => {
  const documentDescription = activity.document_description
  const suggested = activity.suggested_description

  if (!suggested) {
    // suggested_description is null only when GPT found nothing usable anywhere for this
    // activity — not just an empty/unusable Description column, but also nothing usable in the
    // "Describe how this activity promotes/reinforces this" entry it would otherwise fall back
    // on (see buildNameDescriptionMessage's sourceExcerpt handling).
    //
    // documentDescription present here means the column had SOME text that's genuinely
    // nonsensical (keyboard-mash like "bxabsxhkbajkbajs"), not merely short — a short-but-real
    // word (e.g. "meetings") is never nonsensical to contains_placeholder_token?'s dictionary
    // check regardless of length, so it always produces a real suggested_description and never
    // reaches this branch at all. Framed as "doesn't provide enough relevant information" rather
    // than the vaguer "isn't usable" plus a reference to a second column the user can't see or
    // check from this screen.
    // An existing matrix item being filled in manually has no document behind it at all — this
    // step only runs when its own description is genuinely missing (documentDescription is
    // always unset here), so it's a single, simple, source-agnostic prompt.
    let message
    if (activity.isExistingItem) {
      message = "This activity doesn't have a description yet. What should the description be?"
    } else {
      message = documentDescription
        ? `Your description says "${documentDescription}", which doesn't provide enough relevant information for Rei to make a recommendation. Keep it as is, or edit it below.`
        : "Your document's description is empty, and there wasn't enough in its \"Describe how this activity promotes/reinforces this\" entry either for Rei to write one. What should the description be?"
    }
    const options = documentDescription
      ? [
        {
          value: documentDescription,
          label: "Keep document description",
          style: "outline",
        },
      ]
      : []
    options.push({
      value: "__other__",
      label: "Type a description",
      style: "primary",
      freeText: true,
    })
    return botMessage("single_choice", message, {
      activityRef: activity.client_ref,
      field: "description",
      options,
    })
  }

  // suggested_description is only genuinely "from your document" when Rei reused it — compared
  // loosely (via normalizeWords) rather than byte-for-byte, since GPT sometimes reuses a
  // perfectly good description with only trivial polish (capitalizing the first letter,
  // "relationship" -> "relationships", an added period). A strict === comparison would misread
  // that trivial polish as "Rei wrote a whole new one from other details", which is both wrong
  // and implies the document's own (genuinely fine) text "wasn't usable".
  const reusedFromDocument = !!documentDescription
    && normalizeWords(documentDescription) === normalizeWords(suggested)
  let introLine
  if (reusedFromDocument) {
    introLine = "Here's the description from your document. You can keep it or edit it below:"
  } else if (documentDescription) {
    introLine = `Your document's description says "${documentDescription}", which isn't usable as written, so Rei wrote one based on the activity's details. You can keep it or edit it below:`
  } else {
    introLine = "Your document didn't include a description for this activity, so Rei wrote one based on its details. You can keep it or edit it below:"
  }

  // A third "keep the document's own text" option only makes sense when Rei actually wrote a
  // DIFFERENT description (documentDescription present but not reusedFromDocument) — when Rei
  // reused it (verbatim or near-verbatim), or the document had nothing at all, there's no
  // separate original text to offer alongside "Yes, use this". When there IS a genuine
  // difference, "Yes, use this" alone doesn't say which of the two versions it refers to once a
  // second, document-sourced option is also on screen.
  const options = [
    {
      value: suggested,
      label:
        documentDescription && !reusedFromDocument
          ? "Yes, use this (Rei's suggestion)"
          : "Yes, use this",
      style: "primary",
    },
  ]
  if (documentDescription && !reusedFromDocument) {
    options.push({
      value: documentDescription,
      label: "Keep document description",
      style: "outline",
    })
  }
  options.push({
    value: "__other__",
    label: "Edit - Please type a response.",
    style: "outline",
    freeText: true,
  })

  return botMessage(
    "single_choice",
    `${introLine}\n**Description:** ${suggested}\n\n`,
    {
      activityRef: activity.client_ref,
      field: "description",
      options,
    },
  )
}

export const buildDataCollectionQuestionMessage = (activity) => {
  // Trimmed because a trailing space before the closing `**` (common in DB-sourced text)
  // stops markdown parsers from recognizing it as bold, so it shows up as literal asterisks.
  // Deliberately scoped to THIS document's own data only — activity.match.existing_item.
  // data_collection is a different school-saved activity's value from a previous session, not
  // this upload, and mixing it into this message was more confusing than useful.
  const rawAi = activity.suggested_data_collection
  const ai = rawAi ? rawAi.trim() : rawAi
  const options = []

  if (ai) {
    options.push({
      value: ai,
      label: "Yes, add it",
      style: "primary",
    })
  }
  // "Different data" only makes sense when there's a value to differ FROM — with none, there's
  // nothing to be "different" than, so this reads as "Type data" instead, matching the
  // Type-a-name/Type-a-description convention used for other empty fields.
  options.push({
    value: "__other__",
    label: ai
      ? "Different data - Please type a response."
      : "Type data - Please type a response.",
    style: "outline",
    freeText: true,
  })

  const title = "**Data collected to demonstrate effectiveness**:"

  // An existing matrix item being filled in manually has no document behind it at all — this
  // step only runs when its own data collection is genuinely missing (ai is always unset here),
  // so it's a single, simple, source-agnostic prompt instead of the "document" framing below.
  let valueContent
  if (ai) {
    // data_collection_meaningful flags vague/placeholder text (e.g. "n/a", "abc") that
    // technically fills the field but isn't actually usable evidence.
    let note = ""
    if (activity.data_collection_meaningful === true) {
      note = "\n✓ Rei confirms this describes meaningful evidence of effectiveness."
    } else if (activity.data_collection_meaningful === false) {
      note = "\n⚠️ This doesn't appear to describe meaningful evidence — a real method or result, not just a topic word."
    }
    valueContent = `**Your document's data:** ${ai}${note}`
  } else if (activity.isExistingItem) {
    valueContent = "This activity doesn't have any data collection or evidence information yet."
  } else {
    valueContent = "I couldn't find any data collection or evidence information for this activity in your document."
  }

  const content = `${title}\n\n${valueContent}`

  return botMessage("single_choice", content, {
    activityRef: activity.client_ref,
    field: "data_collection",
    options,
  })
}

// Same "complete" definition the rest of the app uses for a saved sel_standard entry (see
// ProgramMatrixConcern#complete_matrix / ProgramMatrixItemsController's incomplete_indicators
// check): a real connection_type AND a non-blank connection_explanation. A previously-saved
// activity can have an entry that's present but incomplete (e.g. saved mid-wizard before this
// step was reached), which is NOT the same as the school having genuinely already chosen an
// indicator here — treating it as "already selected" would compare the user's new upload against
// data that was never actually filled in.
const isCompleteStandard = (info) => !!(info && info.connection_type && info.connection_explanation)

// Explains, wherever the indicators message ends up, why a card isn't backed by this document.
// All three source columns — SEL indicator, Promotes/Reinforces, and the connection-explanation
// description — are checked independently, since any one can be invalid or blank while the
// others are fine (e.g. indicator column empty, connection type column has garbage in it, but
// the description is filled in) — reporting only whichever category happened to be checked first
// and silently dropping the rest would hide part of what's actually wrong. The description column
// is freeform text with no fixed vocabulary to validate against (unlike the other two), so it can
// only ever be blank, never "invalid".
// "A", "A or B", "A, B, or C" — blankParts can now have up to three entries (indicator, type,
// description), where a plain join(" or ") reads as an awkward "A or B or C" run-on.
const joinWithOr = (parts) => {
  if (parts.length < 3) return parts.join(" or ")
  return `${parts.slice(0, -1).join(", ")}, or ${parts[parts.length - 1]}`
}

const documentIndicatorNote = (activity) => {
  const invalidParts = []
  if (activity.invalid_document_sel_indicator) {
    invalidParts.push(
      `an SEL indicator of "${activity.invalid_document_sel_indicator}"`,
    )
  }
  if (activity.invalid_document_connection_type) {
    invalidParts.push(
      `a connection type of "${activity.invalid_document_connection_type}"`,
    )
  }

  // Blank only counts when that same field isn't ALSO the reason it's in invalidParts above —
  // document_sel_indicator/document_connection_type are already guaranteed null whenever their
  // invalid_ counterpart is set, so this just avoids double-reporting the same field twice.
  const blankParts = []
  if (
    !activity.document_sel_indicator
    && !activity.invalid_document_sel_indicator
  ) {
    blankParts.push("an SEL indicator")
  }
  if (
    !activity.document_connection_type
    && !activity.invalid_document_connection_type
  ) {
    blankParts.push("a Promotes/Reinforces type")
  }
  if (!activity.document_connection_explanation) {
    blankParts.push("a description of how it promotes/reinforces this")
  }

  if (invalidParts.length === 0 && blankParts.length === 0) return ""

  const clauses = []
  if (invalidParts.length > 0) clauses.push(`lists ${invalidParts.join(" and ")}, which isn't valid`)
  if (blankParts.length > 0) clauses.push(`doesn't specify ${joinWithOr(blankParts)}`)

  // A document-named SEL indicator code (document_sel_indicator present) still shows below
  // tagged "From your document" regardless of whether its own Promotes/Reinforces type or
  // description were also specified — see reconcile_sel_standards_sources, which only clears
  // those two, never the source. Claiming it's "Rei's own recommendation instead" would
  // contradict that badge; only genuinely true when the document didn't name a valid code at all.
  const conclusion = activity.document_sel_indicator
    ? "so that's left for you to fill in below — you can add more indicators from the list if you'd like."
    : "so the indicator below is Rei's own recommendation instead — you can add more from the list below if you'd like."

  return `Your document ${clauses.join(", and ")}, ${conclusion}`
}

export const buildIndicatorsQuestionMessage = (activity) => {
  const rawExistingStandards = (activity.match
      && activity.match.existing_item
      && activity.match.existing_item.sel_standards)
    || null
  const aiStandards = activity.suggested_sel_standards || {}

  const hasIncompleteExisting = !!rawExistingStandards
    && Object.values(rawExistingStandards).some(
      (info) => !isCompleteStandard(info),
    )
  const existingStandards = rawExistingStandards
    ? Object.fromEntries(
      Object.entries(rawExistingStandards).filter(([, info]) => isCompleteStandard(info)),
    )
    : null

  if (existingStandards && Object.keys(existingStandards).length > 0) {
    const header = "This activity already has indicators selected. Here's what the Rei suggests:"
    const note = documentIndicatorNote(activity)
    return botMessage(
      "keep_or_accept_checklist",
      note ? `${note} ${header}` : header,
      {
        activityRef: activity.client_ref,
        field: "indicators",
        currentStandards: existingStandards,
        aiStandards,
        documentConnectionExplanation: activity.document_connection_explanation,
        documentSelIndicator: activity.document_sel_indicator,
      },
    )
  }

  // "I recommend" overclaims when every indicator came straight from an explicit "SEL
  // indicator"/"Promotes or Reinforces" column — that's transcription, not a judgment call.
  // Provenance is now per-indicator (each entry's own `source`), since the AI adds its own
  // suggestions on top of the document's rather than only filling in when nothing was specified.
  const sources = Object.values(aiStandards).map((info) => info.source)
  const allFromDocument = sources.length > 0 && sources.every((source) => source === "document")
  const anyFromDocument = sources.some((source) => source === "document")
  const indicatorsHeader = (() => {
    if (allFromDocument) return "Here's what your document specifies for indicators:"
    if (anyFromDocument) return "Here's what your document specifies, plus a few more Rei recommends:"
    return "I recommend these indicators:"
  })()
  // hasIncompleteExisting only reaches here when NONE of the existing entries were complete
  // (some-complete falls into the branch above, using only the complete ones) — worth saying
  // explicitly so "here's what your document specifies" doesn't read as ignoring real saved data
  // that, in fact, was never properly filled in.
  const notes = []
  if (hasIncompleteExisting) {
    notes.push(
      "This activity's existing indicators are missing required info (a connection type or explanation), so they're not usable as-is.",
    )
  }
  const documentNote = documentIndicatorNote(activity)
  if (documentNote) notes.push(documentNote)
  // An existing-matrix item being filled in manually (see activityFromExistingItem in
  // useProgramMatrixWizard.js) only ever reaches here with EMPTY aiStandards — there's no
  // document and no AI recommendation to describe, so the usual "document specifies"/"I
  // recommend" framing above would be actively wrong, not just imprecisely worded.
  const content = activity.isExistingItem
    ? "This activity doesn't have any SEL indicators selected yet. Please choose one or more from the list below:"
    : [...notes, indicatorsHeader].join(" ")

  return botMessage("checklist", content, {
    activityRef: activity.client_ref,
    field: "indicators",
    aiStandards,
    documentConnectionExplanation: activity.document_connection_explanation,
    documentSelIndicator: activity.document_sel_indicator,
  })
}

export const buildSummaryMessage = (activity, collected, remaining) => botMessage(
  "summary_card",
  "**Here's the complete activity — ready to save?**",
  {
    activityRef: activity.client_ref,
    field: "summary",
    collected,
    remaining,
  },
)

export const buildSuccessMessage = (name) => buildTextMessage(`✓ ${name} saved!`)

export const buildAnalyzingMessage = () => buildTextMessage("Rei is analyzing your document...")

export const buildErrorMessage = (errorText) => buildTextMessage(
  errorText
      || "Something went wrong while analyzing that document. Please try again.",
)
